import { useConsent } from '@/contexts/CookieConsentContext';
import CookieConsentSettings from './CookieConsentSettings';

export default function CookieConsentBanner() {
  const { showBanner, showSettings, consent, gpcDetected, acceptAll, rejectAll, openSettings, closeSettings } =
    useConsent();

  if (!showBanner) return null;

  // If settings panel is open, render the settings component instead
  if (showSettings) return <CookieConsentSettings />;

  // Shared wrapper: bottom-sheet on mobile (full-width, flush bottom,
  // rounded top only), floating card on sm+ (right-anchored with margin).
  // The previous `fixed bottom-5 right-5 w-full max-w-md` pattern overflowed
  // the mobile viewport and landed over the page content it was meant to
  // float beside.
  const wrapperClasses =
    'fixed z-[9990] bg-white shadow-overlay animate-in slide-in-from-bottom-4 ' +
    'left-0 right-0 bottom-0 rounded-t-lg border-t border-gray-200 p-5 ' +
    'sm:left-auto sm:bottom-5 sm:right-5 sm:max-w-md sm:rounded-md sm:border-0 sm:p-6';

  // GPC detected and auto-resolved: show a brief informational banner
  if (consent && consent.method === 'gpc_auto') {
    return (
      <div role="status" aria-live="polite" className={wrapperClasses}>
        <p className="text-sm text-gray-700 leading-relaxed mb-3">
          We detected your browser's Global Privacy Control signal. Marketing and analytics
          cookies are disabled.{' '}
          <a href="/cookies" className="text-primary hover:underline font-medium">
            Learn more
          </a>
        </p>
        <div className="flex flex-wrap gap-3 items-center">
          <button
            onClick={closeSettings}
            className="text-sm font-medium text-gray-700 hover:text-gray-900 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-sm transition-colors"
          >
            Got it
          </button>
          <button
            onClick={() => acceptAll()}
            className="text-sm font-medium text-primary hover:underline"
          >
            Accept all cookies
          </button>
          <button
            onClick={openSettings}
            className="text-sm font-medium text-gray-500 hover:text-gray-700"
          >
            Change preferences
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Cookie consent"
      className={wrapperClasses}
    >
      <h2 className="text-base font-bold text-gray-900 mb-2">We value your privacy</h2>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        We use cookies to improve your experience, provide customer support, and measure how our
        site is used.{' '}
        <a href="/cookies" className="text-primary hover:underline font-medium">
          Cookie Policy
        </a>
      </p>

      <div className="flex flex-wrap gap-3">
        <button
          onClick={acceptAll}
          className="flex-1 min-w-[120px] min-h-[48px] px-4 py-2.5 bg-primary text-white rounded-sm font-bold text-sm shadow-button-blue hover:shadow-floating hover:bg-black/50 transition-all duration-200"
        >
          Accept All
        </button>
        <button
          onClick={rejectAll}
          className="flex-1 min-w-[120px] min-h-[48px] px-4 py-2.5 bg-background text-foreground rounded-sm font-bold text-sm shadow-button hover:shadow-raised transition-all duration-200"
        >
          Reject All
        </button>
        <button
          onClick={openSettings}
          className="w-full text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors py-1"
        >
          Customize preferences
        </button>
      </div>
    </div>
  );
}
